import { setCurrectChat, setAppointmentAvailability, fetchAppointmentUnreadCount } from "../../../../redux/user-slices/socket.slice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import Icons from "../../../../assets/js/Icons";
import { BASEURI, headers, FILE_PATH_URI } from "../../../../utils/helper";
import axios from "axios";
import moment from "moment";
import {
  Avatar, Box, Chip, Divider, Grid2 as Grid,
  Icon, ListItemText, Paper, Stack, TextField, Badge, Typography
} from "@mui/material";

function AppointmentChatList() {
  const dispatch = useDispatch();
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { socket, chat } = useSelector((state) => state.socketSlice);
  const { member, specialist } = useSelector((state) => state.authSlice);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${BASEURI}/user/appointment-chat/conversations`, {
        headers: headers()
      });
      if (response.data?.success) {
        setRecords(response.data.data);
        filterAndSet(response.data.data, searchQuery);
      }
    } catch (error) {
      console.error("Error fetching appointment conversations:", error);
    }
  };

  const filterAndSet = (list, query) => {
    if (!query.trim()) {
      setFilteredRecords(list);
      return;
    }
    const q = query.toLowerCase();
    const filtered = list.filter(item => {
      const name = item.user?.fullName?.toLowerCase() || "";
      const bookingRef = item.appointmentId ? item.appointmentId.slice(-6).toUpperCase() : "";
      return name.includes(q) || bookingRef.toLowerCase().includes(q);
    });
    setFilteredRecords(filtered);
  };

  useEffect(() => {
    fetchConversations();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!socket) return;

    // Refresh list on receiving a new message or update
    const handleReceiveMessage = () => {
      fetchConversations();
    };

    socket.on("receive-message", handleReceiveMessage);
    socket.on("new-message-notification", handleReceiveMessage);
    socket.on("appointment-completed-by-member", handleReceiveMessage);

    return () => {
      socket.off("receive-message", handleReceiveMessage);
      socket.off("new-message-notification", handleReceiveMessage);
      socket.off("appointment-completed-by-member", handleReceiveMessage);
    };
    // eslint-disable-next-line
  }, [socket]);

  const handleChatActive = async (element) => {
    dispatch(setAppointmentAvailability({
      startTime: element.appointment.startTime,
      endTime: element.appointment.endTime,
      timeZone: element.appointment.timezone
    }));
    dispatch(setCurrectChat(element));
    socket.emit("join-room", element._id);

    // Mark as read in backend
    try {
      await axios.put(`${BASEURI}/user/appointment-chat/read/${element._id}`, {}, {
        headers: headers()
      });
      // Clear unread count locally immediately
      setRecords(prev => prev.map(r => r._id === element._id ? { ...r, unreadCount: 0 } : r));
      setFilteredRecords(prev => prev.map(r => r._id === element._id ? { ...r, unreadCount: 0 } : r));
      dispatch(fetchAppointmentUnreadCount());
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const formatDateAndTime = (startTime) => {
    return moment(startTime).format("MM-DD-YYYY hh:mm A");
  };

  const chipVariants = {
    rejected: "error",
    confirmed: "success",
    completed: "success",
    cancelled: "default",
    late_cancelled: "error",
    no_refund_cancelled: "error",
    expired: "default",
    pending: "warning",
    scheduled: "info",
    no_show_specialist: "warning",
    no_show_member: "warning",
    "no-show": "warning"
  };

  return (
    <Grid size={{ xs: 12, md: "auto" }}>
      <Paper
        sx={{
          gap: 1,
          display: "flex",
          flexDirection: "column",
          height: { xs: "520px", md: "580px" },
          maxHeight: "calc(100vh - 200px)",
          minHeight: "450px",
        }}
        variant="outlined"
        paperVariant="dashboard"
      >
        <TextField
          size="small"
          type="search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            filterAndSet(records, e.target.value);
          }}
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              color: "black",
            },
          }}
          placeholder="Search name or Booking Ref..."
          slotProps={{
            input: {
              startAdornment: (
                <Icon sx={{ fontSize: "18px", mr: 1, color: "#858585" }}>
                  <Icons.Search />
                </Icon>
              ),
            },
          }}
        />
        <Stack
          spacing={0.25}
          sx={{
            width: { md: 300 },
            flex: 1,
            overflowY: "auto",
          }}
          divider={<Divider />}
        >
          {filteredRecords.map((element, i) => (
            <Box
              key={i}
              sx={{
                p: 1.5,
                gap: 1.5,
                display: "flex",
                cursor: "pointer",
                borderRadius: "6px",
                alignItems: "flex-start",
                transition: "all ease 350ms",
                ":hover": { bgcolor: "#00000010" },
                bgcolor: chat?._id === element?._id ? "#00000010" : "",
              }}
              onClick={() => handleChatActive(element)}
            >
              <Avatar
                src={FILE_PATH_URI + element?.user?.image}
                sx={{ width: 44, height: 44, mt: 0.5 }}
              />
              <ListItemText
                sx={{ m: 0 }}
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight="600" fontSize="14px" sx={{ textTransform: "capitalize" }}>
                      {element?.user?.fullName || "Unknown user"}
                    </Typography>
                    {element.unreadCount > 0 && (
                      <Badge badgeContent={element.unreadCount} color="error" />
                    )}
                  </Box>
                }
                secondary={
                  <Box mt={0.5}>
                    <Typography fontSize="12px" color="text.secondary">
                      Ref: {element.appointmentId ? element.appointmentId.slice(-6).toUpperCase() : ""}
                    </Typography>
                    <Typography fontSize="12px" color="text.primary" fontWeight="500">
                      {formatDateAndTime(element.appointment?.startTime)}
                    </Typography>
                    <Box mt={0.5} display="flex" gap={0.5} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={element.appointment?.appointmentStatus?.toUpperCase() || "Pending"}
                        color={chipVariants[element.appointment?.appointmentStatus] || "primary"}
                        sx={{ fontSize: "10px", height: "18px" }}
                      />
                    </Box>
                    {element.lastMessage && (
                      <Box mt={0.5} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography
                          fontSize="11px"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: "150px" }}
                        >
                          {element.lastMessage.type === "text" ? element.lastMessage.text : "Sent a file"}
                        </Typography>
                        <Typography fontSize="9px" color="text.secondary">
                          {moment(element.lastMessage.createdAt).format("h:mm A")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
                slotProps={{
                  secondary: { component: "div" },
                }}
              />
            </Box>
          ))}
          {!filteredRecords.length && (
            <Box p={2} textAlign="center">
              <Typography color="textSecondary" fontSize="14px">
                No appointment chats found.
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Grid>
  );
}

export default AppointmentChatList;
